import { ApplicationType } from '@/contexts/dynamic-config';
import { SmartOrderRole } from '@/enums';
import { useLimitedPerformanceEnabled } from './use-limited-performance';
import {
  useMaybeUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { useSupplierDynamicConfigValue } from './use-dynamic-config-value';

interface OrderInboundsData {
  isOrderAgentOnly: boolean | null;
  isOrderInboundsEnabled: boolean;
  isViewOnly: boolean;
  trainingModeEnabled: boolean;
  unknownContactTriageEnabled: boolean;
}

export const useOrderInbounds = (): OrderInboundsData => {
  const orderInboundsEnabled = useSupplierDynamicConfigValue(
    'sales_rep_ai_orders_enabled',
  );
  const limitedPerformanceEnabled = useLimitedPerformanceEnabled();
  const applicationType = useSupplierDynamicConfigValue('application_type');
  // Prevent training mode from being enabled when it's not visible to the user,
  // even if it was previously enabled and saved to async storage
  const { inInboundTrainingMode } = useUserSettingsContext();
  const maybeUserContext = useMaybeUserContext();

  if (!maybeUserContext) {
    return {
      isOrderAgentOnly: null,
      isOrderInboundsEnabled: false,
      isViewOnly: false,
      trainingModeEnabled: false,
      unknownContactTriageEnabled: false,
    };
  }

  const isSupplierEmployee = Boolean(
    'supplierEmployeeRole' in maybeUserContext &&
      maybeUserContext.supplierEmployeeRole,
  );

  const smartOrderSettings =
    'smartOrderSettings' in maybeUserContext
      ? maybeUserContext.smartOrderSettings
      : undefined;

  return {
    isOrderAgentOnly: Boolean(
      applicationType === ApplicationType.ORDER_AGENT_ONLY ||
        smartOrderSettings?.order_agent_only,
    ),
    isOrderInboundsEnabled: Boolean(
      !limitedPerformanceEnabled &&
        isSupplierEmployee &&
        orderInboundsEnabled &&
        smartOrderSettings?.smart_order_role &&
        smartOrderSettings.smart_order_role !== SmartOrderRole.None,
    ),
    isViewOnly: smartOrderSettings?.smart_order_role === SmartOrderRole.Viewer,
    trainingModeEnabled: Boolean(
      smartOrderSettings?.training_mode_enabled && inInboundTrainingMode,
    ),
    unknownContactTriageEnabled: Boolean(
      smartOrderSettings?.unknown_contact_triage,
    ),
  };
};
